/**
 * «مار» — THE RULES, as a pure engine: no DOM, no timers, no network, no `Math.random` of its own.
 *
 * Why it lives in the web app and not in `packages/engines/`: Snake is played entirely on the phone,
 * so the web bundle is its only reader — and a new workspace package is not free here. Both
 * Dockerfiles copy every workspace manifest by name before `pnpm install`
 * (`infra/test/dockerfile-manifests.test.ts` fails if one is missing), and the lockfile change would
 * move the install layer of all five images for a module only the web imports.
 *
 * Everything that varies is INJECTED: the board's size, the snake's starting length, a food's and a
 * bonus's growth, the points a food is worth (the chosen speed's), the bonus's four numbers, and the
 * randomness. The app passes the shared rules (`SNAKE_*` in `@bing/shared`, where the api derives its
 * score bounds from the same numbers); the tests pass tiny boards.
 *
 * THE RULES (the operator's, 2026-11-02):
 *  - A square board with NO WALLS: a head leaving an edge comes back in at the opposite edge, in the
 *    same row or column.
 *  - The snake starts `initialLength` long, horizontal, in the middle row, heading right.
 *  - A normal food sits on a uniformly random EMPTY cell — not the snake, not the bonus. Eating it
 *    adds the speed's points and `growth` cells to the snake's PENDING GROWTH. While growth is
 *    pending — from the step that eats on — every step holds the tail still and uses up one cell of
 *    it, so the snake grows `growth` cells over that many steps.
 *  - After every `bonusEvery`-th NORMAL food (5, 10, 15…) a BONUS appears: a `bonusSize`² block of
 *    empty cells wholly inside the board (it never wraps an edge), not on the snake and not on the
 *    normal food, chosen uniformly among every such block. No such block → no bonus that time. A new
 *    bonus REPLACES one still on the board: the old one is removed and the new one is chosen with the
 *    old one's cells free again (they are always empty — the snake can only enter them by eating it,
 *    and food is never placed on them — so a replacement always finds room).
 *  - A bonus lasts `bonusSteps` steps and is worth `points × ⌈stepsLeft / bonusTick⌉`. Every LATER
 *    step moves the snake first: a head that lands on any of its cells earns that value (the one shown
 *    before the step), adds `bonusGrowth` cells to the pending growth (twice a food's) and removes the
 *    bonus; otherwise `stepsLeft` drops by one and the bonus vanishes at 0. Only normal foods count
 *    towards the next bonus.
 *  - The game ends ONLY when the head runs into the snake's own body — the cell the tail leaves on
 *    the same step is free, unless the tail holds still on that step (growth pending, or an eat) — or
 *    when the snake fills the board, no empty cell left: a WIN (`full`), never a crash (`self`).
 *  - A food that finds no empty cell (a bonus holding the last free ones) waits: it is placed on the
 *    first step that frees a cell.
 *
 * THE INPUT QUEUE is what makes quick taps never lost and a reversal impossible: up to
 * {@link SNAKE_TURN_QUEUE} pending turns, each checked against the LAST QUEUED direction (or the
 * heading when none is queued) — the same direction or its opposite is ignored, anything else is
 * appended — and every step consumes one. Each queued turn is a quarter turn from the one before it,
 * so no step can ever point the head back into the neck.
 */

export type SnakeDir = "up" | "down" | "left" | "right";

/** How many turns may wait for their step. A fifth tap inside one step is dropped. */
export const SNAKE_TURN_QUEUE = 4;

const OPPOSITE: Readonly<Record<SnakeDir, SnakeDir>> = {
  up: "down",
  down: "up",
  left: "right",
  right: "left",
};

const DELTA: Readonly<Record<SnakeDir, readonly [dx: number, dy: number]>> = {
  up: [0, -1],
  down: [0, 1],
  left: [-1, 0],
  right: [1, 0],
};

/** The numbers one game is played by. Cells are numbered row-major: `y * grid + x`. */
export interface SnakeRules {
  /** Cells per side of the square board. */
  readonly grid: number;
  /** Cells the snake starts with. */
  readonly initialLength: number;
  /** Cells one normal food grows the snake by. */
  readonly growth: number;
  /** Cells one bonus grows the snake by. */
  readonly bonusGrowth: number;
  /** Points one normal food is worth — the chosen speed's. */
  readonly points: number;
  /** A bonus appears after every `bonusEvery`-th normal food. */
  readonly bonusEvery: number;
  /** Steps a bonus lasts. */
  readonly bonusSteps: number;
  /** A bonus's value drops by `points` every `bonusTick` steps: `points × ⌈stepsLeft / bonusTick⌉`. */
  readonly bonusTick: number;
  /** A bonus is a `bonusSize × bonusSize` block. */
  readonly bonusSize: number;
}

/**
 * THE RANDOMNESS: pick one of `candidates` (never empty). The engine lists every legal cell itself —
 * every empty cell for a food, the top-left cell of every free in-board block for a bonus — so the
 * only thing left to chance is which one, and a uniform pick makes the placement uniform.
 */
export type SnakeChooser = (candidates: readonly number[], kind: "food" | "bonus") => number;

/** The app's chooser: uniform over the candidates, from a `[0, 1)` source (`Math.random`). */
export function uniformChooser(random: () => number = Math.random): SnakeChooser {
  return (candidates) => {
    const i = Math.min(
      candidates.length - 1,
      Math.max(0, Math.floor(random() * candidates.length)),
    );
    return candidates[i] as number;
  };
}

/** Why a game ended: the head ran into the body (a crash), or the snake filled the board (a win). */
export type SnakeEnd = "self" | "full";

/** A bonus on the board. */
export interface SnakeBonus {
  /** The block's top-left cell. */
  readonly cell: number;
  /** Steps left before it vanishes. */
  readonly stepsLeft: number;
}

/**
 * WHAT ONE STEP CHANGED — exactly the cells the board must repaint, so a step never redraws the rest.
 * Paint in this order: the gone bonus, the tail, the neck, the head, the food, the new bonus (a
 * head that ate the bonus stands on a cell of the block being erased).
 */
export interface SnakeStep {
  /** False on the step that ended the game by a collision: nothing moved. */
  readonly moved: boolean;
  /** The new head — or, when `moved` is false, the cell the head ran into. */
  readonly head: number;
  /** The previous head, now body. */
  readonly neck: number;
  /** The cell the tail left, or null when the tail held still (growth, or no move). */
  readonly tail: number | null;
  /** What the head ate on this step. */
  readonly ate: "food" | "bonus" | null;
  /** A food placed on this step (null: the food did not move). */
  readonly food: number | null;
  /** The top-left cell of a bonus removed on this step (eaten, expired or replaced). */
  readonly bonusGone: number | null;
  /** The top-left cell of a bonus placed on this step. */
  readonly bonusNew: number | null;
  /** The bonus's value after this step — `points × ⌈stepsLeft / bonusTick⌉` — or 0 when none is on
   *  the board. */
  readonly bonusValue: number;
  /** Points earned on this step. */
  readonly scored: number;
  /** The score after this step. */
  readonly score: number;
  /** Why the game ended on this step, or null while it goes on. */
  readonly over: SnakeEnd | null;
}

export class SnakeGame {
  readonly rules: SnakeRules;
  private readonly choose: SnakeChooser;
  /** 1 where the snake is. */
  private readonly occupied: Uint8Array;
  /** The snake, tail first, head last. */
  private readonly body: number[] = [];
  private heading: SnakeDir = "right";
  private readonly queue: SnakeDir[] = [];
  private foodCell: number | null = null;
  private bonusBlock: { cell: number; stepsLeft: number } | null = null;
  /** Normal foods eaten — the bonus's clock. */
  private eaten = 0;
  /** Cells the snake has still to grow: the tail holds still while any are left. */
  private pending = 0;
  private points = 0;
  private ended: SnakeEnd | null = null;

  constructor(rules: SnakeRules, choose: SnakeChooser = uniformChooser()) {
    const { grid, initialLength, growth, bonusGrowth, bonusSize, bonusTick } = rules;
    if (!Number.isInteger(grid) || grid < 2) throw new RangeError(`grid ${grid}`);
    if (!Number.isInteger(initialLength) || initialLength < 1 || initialLength > grid) {
      throw new RangeError(`initialLength ${initialLength}`);
    }
    if (!Number.isInteger(growth) || growth < 1) throw new RangeError(`growth ${growth}`);
    if (!Number.isInteger(bonusGrowth) || bonusGrowth < 1) {
      throw new RangeError(`bonusGrowth ${bonusGrowth}`);
    }
    if (!Number.isInteger(bonusSize) || bonusSize < 1 || bonusSize > grid) {
      throw new RangeError(`bonusSize ${bonusSize}`);
    }
    if (!Number.isInteger(bonusTick) || bonusTick < 1) {
      throw new RangeError(`bonusTick ${bonusTick}`);
    }
    this.rules = rules;
    this.choose = choose;
    this.occupied = new Uint8Array(grid * grid);
    // Horizontal, in the middle row, heading right: the head on the centre column where it fits.
    const row = Math.floor(grid / 2);
    const headX = Math.min(grid - 1, Math.max(Math.floor(grid / 2), initialLength - 1));
    for (let x = headX - initialLength + 1; x <= headX; x++) {
      const cell = row * grid + x;
      this.body.push(cell);
      this.occupied[cell] = 1;
    }
    if (this.body.length === grid * grid) this.ended = "full";
    else this.foodCell = this.placeFood();
  }

  /** The snake's cells, tail first, head last. */
  get cells(): readonly number[] {
    return this.body;
  }

  get head(): number {
    return this.body[this.body.length - 1] as number;
  }

  /** The direction of the last step (or the start). */
  get direction(): SnakeDir {
    return this.heading;
  }

  /** The turns waiting for their step, oldest first. */
  get queued(): readonly SnakeDir[] {
    return this.queue;
  }

  get food(): number | null {
    return this.foodCell;
  }

  get bonus(): SnakeBonus | null {
    return this.bonusBlock ? { ...this.bonusBlock } : null;
  }

  /** The bonus's value right now — `points × ⌈stepsLeft / bonusTick⌉` — or 0 when none is on the
   *  board. */
  get bonusValue(): number {
    const block = this.bonusBlock;
    if (!block) return 0;
    return this.rules.points * Math.ceil(block.stepsLeft / this.rules.bonusTick);
  }

  /** Every cell of a bonus whose top-left cell is `cell`. */
  bonusCells(cell: number): number[] {
    const { grid, bonusSize } = this.rules;
    const x0 = cell % grid;
    const y0 = Math.floor(cell / grid);
    const out: number[] = [];
    for (let y = y0; y < y0 + bonusSize; y++) {
      for (let x = x0; x < x0 + bonusSize; x++) out.push(y * grid + x);
    }
    return out;
  }

  get score(): number {
    return this.points;
  }

  /** Normal foods eaten so far. */
  get foodsEaten(): number {
    return this.eaten;
  }

  /** Cells the snake has still to grow. */
  get growing(): number {
    return this.pending;
  }

  get over(): SnakeEnd | null {
    return this.ended;
  }

  /**
   * A tap. Checked against the LAST QUEUED direction (the heading when nothing is queued): the same
   * direction or its opposite is ignored, anything else waits for its step. Returns whether it was
   * queued.
   */
  turn(dir: SnakeDir): boolean {
    if (this.ended) return false;
    const last = this.queue[this.queue.length - 1] ?? this.heading;
    if (dir === last || dir === OPPOSITE[last]) return false;
    if (this.queue.length >= SNAKE_TURN_QUEUE) return false;
    this.queue.push(dir);
    return true;
  }

  /** One step of the snake. A finished game does not step. */
  step(): SnakeStep {
    if (this.ended) throw new Error("the game is over");
    const { grid, growth, bonusGrowth, points, bonusEvery, bonusSteps } = this.rules;
    const next = this.queue.shift();
    if (next) this.heading = next;

    const neck = this.head;
    const [dx, dy] = DELTA[this.heading];
    const x = ((((neck % grid) + dx) % grid) + grid) % grid;
    const y = (((Math.floor(neck / grid) + dy) % grid) + grid) % grid;
    const head = y * grid + x;

    const onFood = head === this.foodCell;
    const onBonus = this.bonusBlock !== null && this.inBonus(head);
    // The tail holds still on a step that eats and on every step while growth is pending.
    const grows = onFood || onBonus || this.pending > 0;
    const tailCell = this.body[0] as number;

    // THE ONE WAY TO LOSE: the head on the body. The tail's cell is free on this step — it moves out
    // as the head moves in — unless the tail holds still on it.
    if (this.occupied[head] === 1 && !(head === tailCell && !grows)) {
      this.ended = "self";
      return {
        moved: false,
        head,
        neck,
        tail: null,
        ate: null,
        food: null,
        bonusGone: null,
        bonusNew: null,
        bonusValue: this.bonusValue,
        scored: 0,
        score: this.points,
        over: "self",
      };
    }

    if (onFood) this.pending += growth;
    if (onBonus) this.pending += bonusGrowth;
    let tail: number | null = null;
    if (grows) {
      this.pending -= 1;
    } else {
      tail = this.body.shift() as number;
      this.occupied[tail] = 0;
    }
    this.body.push(head);
    this.occupied[head] = 1;

    let scored = 0;
    let bonusGone: number | null = null;
    // The bonus first: eaten, or one step older — whatever else this step does.
    if (this.bonusBlock) {
      if (onBonus) {
        scored += this.bonusValue;
        bonusGone = this.bonusBlock.cell;
        this.bonusBlock = null;
      } else {
        this.bonusBlock.stepsLeft -= 1;
        if (this.bonusBlock.stepsLeft <= 0) {
          bonusGone = this.bonusBlock.cell;
          this.bonusBlock = null;
        }
      }
    }
    if (onFood) {
      scored += points;
      this.eaten += 1;
      this.foodCell = null;
    }
    this.points += scored;

    if (this.body.length === grid * grid) {
      this.ended = "full";
      return {
        moved: true,
        head,
        neck,
        tail,
        ate: onFood ? "food" : onBonus ? "bonus" : null,
        food: null,
        bonusGone,
        bonusNew: null,
        bonusValue: 0,
        scored,
        score: this.points,
        over: "full",
      };
    }

    // The food comes back on an empty cell — on this step if it was eaten, or as soon as a cell is
    // free if a bonus had taken the last ones.
    const food = this.foodCell === null ? this.placeFood() : null;
    if (food !== null) this.foodCell = food;

    // Every `bonusEvery`-th normal food brings a bonus, replacing one still on the board.
    let bonusNew: number | null = null;
    if (onFood && this.eaten % bonusEvery === 0) {
      if (this.bonusBlock) {
        bonusGone = this.bonusBlock.cell;
        this.bonusBlock = null;
      }
      const cell = this.placeBonus();
      if (cell !== null) {
        this.bonusBlock = { cell, stepsLeft: bonusSteps };
        bonusNew = cell;
      }
    }

    return {
      moved: true,
      head,
      neck,
      tail,
      ate: onFood ? "food" : onBonus ? "bonus" : null,
      food,
      bonusGone,
      bonusNew,
      bonusValue: this.bonusValue,
      scored,
      score: this.points,
      over: null,
    };
  }

  private inBonus(cell: number): boolean {
    const block = this.bonusBlock;
    if (!block) return false;
    const { grid, bonusSize } = this.rules;
    const dx = (cell % grid) - (block.cell % grid);
    const dy = Math.floor(cell / grid) - Math.floor(block.cell / grid);
    return dx >= 0 && dx < bonusSize && dy >= 0 && dy < bonusSize;
  }

  /** A uniformly chosen empty cell (not the snake, not the bonus), or null when there is none. */
  private placeFood(): number | null {
    const total = this.rules.grid * this.rules.grid;
    const empty: number[] = [];
    for (let cell = 0; cell < total; cell++) {
      if (this.occupied[cell] === 0 && !this.inBonus(cell)) empty.push(cell);
    }
    if (empty.length === 0) return null;
    return this.pick(empty, "food");
  }

  /** The top-left cell of a uniformly chosen free in-board block, or null when there is none. */
  private placeBonus(): number | null {
    const { grid, bonusSize } = this.rules;
    const free = (cell: number): boolean => this.occupied[cell] === 0 && cell !== this.foodCell;
    const blocks: number[] = [];
    for (let y = 0; y + bonusSize <= grid; y++) {
      for (let x = 0; x + bonusSize <= grid; x++) {
        const corner = y * grid + x;
        if (this.bonusCells(corner).every(free)) blocks.push(corner);
      }
    }
    if (blocks.length === 0) return null;
    return this.pick(blocks, "bonus");
  }

  private pick(candidates: number[], kind: "food" | "bonus"): number {
    const chosen = this.choose(candidates, kind);
    // A chooser that answers off the list cannot put anything on an illegal cell.
    return candidates.includes(chosen) ? chosen : (candidates[0] as number);
  }
}
